"use client";

import { create } from "zustand";
import { toast } from "sonner";
import { storageService } from "@/services/storage/service";
import type { BannerMetadata, BannerTransform } from "@/services/storage/types";
import { getImageDimensions, getVideoDimensionsAndDuration } from "@/media/get-media-dimensions";

export interface Banner {
	metadata: BannerMetadata;
	/** Kept resident in memory (not re-read from OPFS on every add/drag) — same reasoning as
	 * UserSound.file in sounds/user-sounds-store.ts: "add to timeline" needs to be synchronous up
	 * to the point of calling editor.media.addMediaAsset for a native HTML5 drag to work. */
	file: File;
	/** Object URL for the library thumbnail/preview — revoked on remove/unmount, not persisted. */
	url: string;
}

interface BannerStore {
	banners: Banner[];
	isLoaded: boolean;
	isUploading: boolean;
	load: () => Promise<void>;
	upload: (args: { files: File[] }) => Promise<void>;
	remove: (args: { id: string }) => Promise<void>;
	/** Called by banner-position-tracker.ts whenever a banner-sourced element's transform
	 * settles (drag/resize/rotate end, or any other committed change) — updates both the
	 * persisted metadata and this in-memory copy so the library list reflects it immediately. */
	rememberTransform: (args: { id: string; transform: BannerTransform }) => Promise<void>;
}

/** "Баннеры" — a personal library of advertising banner assets (image or video; no animated-GIF
 * playback — a GIF uploaded here is treated as a static image, see upload() below), global across
 * all projects like "Мои звуки" (sounds/user-sounds-store.ts). Backed by storageService's global
 * banners OPFS/IndexedDB pair. Adding a banner to a project's timeline copies it into that
 * project's own MediaAsset store (see banners/materialize-banner-asset.ts) and applies its
 * lastTransform, if any — see banners/components/banners-view.tsx and
 * banners/banner-position-tracker.ts for the two halves of the "remember position" feature. */
export const useBannerStore = create<BannerStore>((set, get) => ({
	banners: [],
	isLoaded: false,
	isUploading: false,

	load: async () => {
		if (get().isLoaded) return;
		try {
			const items = await storageService.loadBanners();
			const banners = items.map(({ metadata, file }) => ({
				metadata,
				file,
				url: URL.createObjectURL(file),
			}));
			set({ banners, isLoaded: true });
		} catch (error) {
			console.error("Failed to load banners:", error);
			set({ isLoaded: true });
		}
	},

	upload: async ({ files }) => {
		set({ isUploading: true });
		try {
			for (const file of files) {
				// Browsers report a GIF's MIME type as "image/gif" — same bucket as any other still
				// image here, since this app has no animated-GIF decode/playback path (see
				// banner-store.ts's own top comment). It still uploads and previews fine, just frozen
				// on its first frame, same as any other image.
				const isImage = file.type.startsWith("image/");
				const isVideo = file.type.startsWith("video/");
				if (!isImage && !isVideo) {
					toast.error(`Не изображение и не видео: ${file.name}`);
					continue;
				}

				const storageCheck = await storageService.canStoreFile({ size: file.size });
				if (!storageCheck.canStore) {
					toast.error(`Недостаточно места в хранилище браузера для ${file.name}`);
					continue;
				}

				try {
					const { width, height, duration } = isImage
						? await getImageDimensions({ file })
						: await getVideoDimensionsAndDuration({ file });

					const metadata = await storageService.saveBanner({
						file,
						name: file.name,
						mediaType: isImage ? "image" : "video",
						width,
						height,
						duration,
					});
					set((state) => ({
						banners: [
							...state.banners,
							{ metadata, file, url: URL.createObjectURL(file) },
						],
					}));
				} catch (error) {
					console.error("Failed to upload banner:", error);
					toast.error(`Не удалось загрузить ${file.name}`, {
						description: error instanceof Error ? error.message : undefined,
					});
				}
			}
		} finally {
			set({ isUploading: false });
		}
	},

	remove: async ({ id }) => {
		const target = get().banners.find((banner) => banner.metadata.id === id);
		try {
			await storageService.removeBanner({ id });
			if (target) {
				URL.revokeObjectURL(target.url);
			}
			set((state) => ({
				banners: state.banners.filter((banner) => banner.metadata.id !== id),
			}));
		} catch (error) {
			console.error("Failed to remove banner:", error);
			toast.error("Не удалось удалить баннер");
		}
	},

	rememberTransform: async ({ id, transform }) => {
		try {
			await storageService.updateBannerTransform({ id, transform });
			set((state) => ({
				banners: state.banners.map((banner) =>
					banner.metadata.id === id
						? { ...banner, metadata: { ...banner.metadata, lastTransform: transform } }
						: banner,
				),
			}));
		} catch (error) {
			// Silent — this fires automatically on every drag/resize end (see
			// banner-position-tracker.ts), so a toast here would fire constantly for a
			// non-critical, best-effort convenience feature. The console log is enough to debug.
			console.error("Failed to remember banner position:", error);
		}
	},
}));
